import React from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar, Box, Chip, Tooltip, Typography } from '@mui/material';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import MailOutlined from '@mui/icons-material/MailOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';

const getInitials = (prenom = '', nom = '') =>
  `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase();

// The API sometimes sends the string "null" or "N/A" instead of an empty value.
const clean = (value) => {
  if (value === null || value === undefined) return '';
  const s = String(value).trim();
  return ['', 'null', 'n/a', 'undefined'].includes(s.toLowerCase()) ? '' : s;
};

const CONTRACT_STYLES = {
  cdi: { bg: '#ebf4fb', color: '#03407b', border: '#aed6f1' },
  cdd: { bg: '#fdf2f8', color: '#9d174d', border: '#fbcfe8' },
  stage: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  stagiaire: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  freelance: { bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
};
const contractStyle = (name) => CONTRACT_STYLES[name?.toLowerCase()] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };

const daysUntil = (date) => Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
const plural = (n) => `${n} jour${n > 1 ? 's' : ''}`;

// Warning 15 days before the contract ends; alert once a non-CDI contract has ended.
const getContractAlert = (employee) => {
  if (!employee.dateFinContrat) return null;
  const diffDays = daysUntil(employee.dateFinContrat);
  const type = employee.contrat?.nom_contrat?.toLowerCase() || '';
  const soon = diffDays >= 0 && diffDays <= 15;
  const when = diffDays === 0 ? "aujourd'hui" : `dans ${plural(diffDays)}`;

  if (type === 'cdi') {
    if (!soon) return null;
    return employee.nombreRenouvellements === 0
      ? { level: 'warning', text: `Période d'essai se termine ${when}` }
      : { level: 'error', text: `Contrat CDI renouvelé se termine ${when}` };
  }
  if (soon) return { level: 'error', text: `Contrat se termine ${when}` };
  if (diffDays < 0) return { level: 'error', text: 'Contrat arrivé à son terme — à vérifier' };
  return null;
};

const ALERT_COLORS = {
  warning: { bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
  error: { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5' },
};

const InfoRow = ({ icon: Icon, label, children, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
    <Icon sx={{ fontSize: 17, color: '#94a3b8', flexShrink: 0 }} />
    <Typography component="span" sx={{ fontSize: '0.72rem', color: '#94a3b8', width: 72, flexShrink: 0 }}>{label}</Typography>
    <Box title={title} sx={{ minWidth: 0, flex: 1, fontSize: '0.85rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  </Box>
);

const Missing = ({ children }) => (
  <Box component="span" sx={{ color: '#cbd5e1' }}>{children}</Box>
);

const CollaborateurCard = ({ employee }) => {
  const history = useHistory();
  const open = () => history.push(`/collaborateurs/${employee.id}/view`);

  const fullName = [clean(employee.titre), clean(employee.prenom), clean(employee.nom)].filter(Boolean).join(' ');
  const fonction = clean(employee.fonction);
  const phone = clean(employee.telephone);
  const email = clean(employee.email);
  const integration = clean(employee.dateIntegration);
  const contractName = clean(employee.contrat?.nom_contrat);
  const hasContract = contractName && contractName.toLowerCase() !== 'aucun';
  const cs = contractStyle(contractName);
  const alert = getContractAlert(employee);
  const alertColors = alert && ALERT_COLORS[alert.level];

  // Links inside the card must not also open the profile.
  const stop = (e) => e.stopPropagation();

  const contractChip = hasContract ? (
    <Chip label={contractName.toUpperCase()} size="small"
      sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.04em', background: cs.bg, color: cs.color, border: `1px solid ${cs.border}` }} />
  ) : (
    <Tooltip title="Sans contrat : ce collaborateur n'a pas encore de contrat." arrow>
      <Box sx={{ width: 24, height: 24, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fffbeb', border: '1px dashed #fcd34d', color: '#b45309', cursor: 'help' }}>
        <WarningAmberIcon sx={{ fontSize: 15 }} />
      </Box>
    </Tooltip>
  );

  return (
    <Box
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => e.key === 'Enter' && open()}
      sx={{
        height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.75, cursor: 'pointer',
        borderRadius: '14px', background: '#fff',
        border: `1px solid ${alertColors ? alertColors.border : '#e2e8f0'}`,
        boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
        transition: 'all 0.18s ease',
        '&:hover': { borderColor: '#aed6f1', boxShadow: '0 8px 24px rgba(3,64,123,0.10)', transform: 'translateY(-2px)' },
        '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Avatar src={employee.imageUrl || undefined} alt={fullName}
          sx={{ width: 44, height: 44, fontSize: '0.9rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b', border: '1px solid #aed6f1' }}>
          {getInitials(employee.prenom, employee.nom)}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fullName}>
            {fullName || '—'}
          </Typography>
          <Typography title={fonction} sx={{
            fontSize: '0.8rem', color: '#64748b', lineHeight: 1.35, mt: 0.25,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {fonction || <Missing>Fonction non renseignée</Missing>}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>{contractChip}</Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 1.25, py: 1, borderRadius: '10px', background: '#f8fafc' }}>
        <InfoRow icon={PhoneOutlined} label="Téléphone" title={phone}>
          {phone
            ? <a href={`tel:${phone.replace(/\s/g, '')}`} onClick={stop} style={{ color: 'inherit', textDecoration: 'none' }}>{phone}</a>
            : <Missing>Non renseigné</Missing>}
        </InfoRow>
        <InfoRow icon={MailOutlined} label="Email" title={email}>
          {email
            ? <a href={`mailto:${email}`} onClick={stop} style={{ color: '#0369a1', textDecoration: 'none' }}>{email}</a>
            : <Missing>Non renseigné</Missing>}
        </InfoRow>
        <InfoRow icon={EventAvailableOutlined} label="Intégration">
          {integration ? (isNaN(new Date(integration)) ? integration : new Date(integration).toLocaleDateString('fr-FR')) : (
            <Tooltip title="Ce collaborateur n'a pas encore de date d'intégration." arrow>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#b45309', cursor: 'help' }}>
                <WarningAmberIcon sx={{ fontSize: 15 }} /> À renseigner
              </Box>
            </Tooltip>
          )}
        </InfoRow>
      </Box>

      {alert && (
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.6, borderRadius: '8px', background: alertColors.bg, color: alertColors.color, fontSize: '0.78rem', fontWeight: 600 }}>
          {alert.level === 'error' ? <ErrorOutlined sx={{ fontSize: 16 }} /> : <WarningAmberIcon sx={{ fontSize: 16 }} />}
          {alert.text}
        </Box>
      )}
    </Box>
  );
};

export default CollaborateurCard;
